import { Button, message, Switch, Tabs, Row, Col, Table } from 'antd'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAsync } from '../../../hooks'
import useExportExcel from '../../../hooks/useExportExcel'
import { formatDOB } from '../../../lib/datetime'
import {
  markBillingOrderAsBilled,
  useBillingOrders,
  useBillingOrdersPagination,
  useFetchBillingOrders,
  useFetchCPTCodes,
  useCPTCodes,
} from '../../../redux/slices/in-office-billing-orders'
import DownloadLink from '../../Common/DownloadLink'
import { toPath } from '../../../routes'
import { ListError } from '../../Common'

const { TabPane } = Tabs

const COLUMNS = [
  {
    title: 'Last Name',
    dataIndex: 'patient.lastName',
    key: 'lastName',
  },
  {
    title: 'First Name',
    dataIndex: 'patient.firstName',
    key: 'firstName',
  },
  {
    title: 'MRN',
    key: 'MRN',
    dataIndex: 'patient.practiceMrn',
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: 'patient.birthDate',
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: 'device.deviceModel.deviceType.name',
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Billing Date',
    dataIndex: 'billingDate',
    key: 'billingDate',
    render: formatDOB,
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, billingOrder) {
      if (!billingOrder.preAuthorization) return null

      const {
        insuranceAuthorizationFilePath,
        insuranceAuthorizationNumber,
      } = billingOrder.preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render(text, billingOrder) {
      return (
        <Link
          to={toPath.billing.officeBillingOrders.officeBillingOrder({
            id: billingOrder.id,
          })}
        >
          <Button type="link" icon="file-search">
            Comments / More
          </Button>
        </Link>
      )
    },
  },
]

const CPT_CODE_COLUMNS = [
  {
    title: 'CPT Codes',
    dataIndex: 'cptCode',
    key: 'cptCode',
    width: '80%',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
  },
]

export default function BillingOrders({
  checkDate,
  onPageChange,
  page,
  practiceId,
  query,
}) {
  const dispatch = useDispatch()
  const billingOrders = useBillingOrders(checkDate, query)
  const { pageSize, total } = useBillingOrdersPagination(checkDate, query)
  const cptCodes = useCPTCodes()
  const {
    pending: cptPending,
    error: cptError,
    clearError: clearCPTError,
  } = useAsync(useFetchCPTCodes(practiceId))
  const { pending, error, clearError } = useAsync(
    useFetchBillingOrders(practiceId, checkDate, page, query),
  )

  const currentCPTCodes = useMemo(() => {
    const current = cptCodes.find(entry => entry.practiceId === practiceId)

    if (current && current.data) {
      return current.data
    }
    return []
  }, [cptCodes, practiceId])

  const handleBilledChange = useCallback(
    async (id, billed) => {
      try {
        await dispatch(markBillingOrderAsBilled(id, billed))
        message.success('Billing Order has been updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.billed',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const copy = [...COLUMNS]
    copy.splice(copy.length - 1, 0, {
      title: 'Billed',
      key: 'billed',
      render: (text, record) => {
        return (
          <Switch
            checked={record.billed}
            checkedChildren="Billed"
            unCheckedChildren="Not&nbsp;Billed"
            onChange={billed => handleBilledChange(record.id, billed)}
          />
        )
      },
    })
    return copy
  }, [handleBilledChange])

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/in-office-billing-orders.xlsx?date=${checkDate}`,
  )

  return (
    <Tabs defaultActiveKey="1" style={{ marginBottom: 16 }}>
      <TabPane tab="Billing Orders" key="1">
        {error && (
          <ListError description={error.message} onClose={clearError} />
        )}
        <Table
          size="middle"
          columns={columns}
          dataSource={billingOrders}
          rowKey="id"
          loading={pending}
          pagination={{
            current: page,
            pageSize,
            onChange: onPageChange,
            total,
          }}
        />
        {Boolean(billingOrders.length) && (
          <div>
            <Button
              loading={exportingExcel}
              onClick={exportExcel}
              icon="file-excel"
            >
              Export this list to Excel
            </Button>
          </div>
        )}
      </TabPane>
      <TabPane tab="CPT Codes Summary" key="2">
        {cptError && (
          <ListError description={cptError.message} onClose={clearCPTError} />
        )}
        <Row>
          <Col span={12}>
            <Table
              size="middle"
              columns={CPT_CODE_COLUMNS}
              dataSource={currentCPTCodes}
              rowKey="id"
              loading={cptPending}
            />
          </Col>
        </Row>
      </TabPane>
    </Tabs>
  )
}

BillingOrders.propTypes = {
  checkDate: PropTypes.string.isRequired,
  practiceId: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number,
  query: PropTypes.string,
}

BillingOrders.defaultProps = {
  page: 1,
  query: '',
}
